import { CLIENT_BY_ID } from '../../data/clientLoyalty';

/**
 * clientServicePenalties — « plus d'impact avec les clients » (REX apprenant).
 * Une gestion approximative des contrats (livraisons manquées) coûte désormais :
 *   • toutes les 3 livraisons manquées sur un contrat : pénalité contractuelle
 *     (2 jours de CA, min 300 €) + réputation −1 (−2 dès 6 manquées) +
 *     fidélité client −6 (`service_penalty`)
 *   • toutes les 6 livraisons manquées : bouche-à-oreille négatif — les autres
 *     clients du même segment perdent 2 pts de fidélité, réputation −2.
 */
export const PENALTY_EVERY_MISSES = 3;
export const WORD_OF_MOUTH_EVERY_MISSES = 6;
export const PENALTY_DAYS_OF_REVENUE = 2;
export const PENALTY_MIN = 300;

export function computeServicePenalties(activeContracts = [], contractMisses = {}, clientLoyalty = {}) {
  const out = [];
  activeContracts.forEach((c) => {
    const marker = contractMisses[c.instanceId];
    // 'tolerated' = absence courte sur contrat long (Lot N) : aucune pénalité.
    if (!marker || marker === 'tolerated') return;
    const m = c.missedDeliveries || 0;
    if (m <= 0 || m % PENALTY_EVERY_MISSES !== 0) return;
    const daily = Number(c.dailyRevenue) || Number(c.revenue) || 0;
    const penalty = Math.max(PENALTY_MIN, Math.round(daily * PENALTY_DAYS_OF_REVENUE));
    // Absence planifiée (congés annoncés) : le client était prévenu, pas de bouche-à-oreille.
    const wordOfMouth = m % WORD_OF_MOUTH_EVERY_MISSES === 0 && marker !== 'long_planned';
    const client = c.clientId ? CLIENT_BY_ID[c.clientId] : null;
    const affectedClientIds = wordOfMouth && client
      ? Object.keys(clientLoyalty).filter((id) => id !== client.id && CLIENT_BY_ID[id]?.segment === client.segment)
      : [];
    out.push({
      contractId: c.instanceId,
      contractName: c.name,
      clientId: c.clientId || null,
      clientName: client?.name || c.clientName || c.client || 'Client',
      missedDays: m,
      penalty,
      repHit: (m >= WORD_OF_MOUTH_EVERY_MISSES ? 2 : 1) + (wordOfMouth ? 2 : 0),
      wordOfMouth,
      affectedClientIds,
    });
  });
  return out;
}

export function buildPenaltyMessage(p, day) {
  const wom = p.wordOfMouth
    ? `\n\n⚠️ Nous avons partagé notre expérience avec nos confrères du secteur. Votre fiabilité est désormais remise en question auprès de ${p.affectedClientIds.length} autre(s) donneur(s) d'ordre.`
    : '';
  return {
    senderType: 'client',
    senderId: p.clientId || undefined,
    senderName: p.clientName,
    tone: 'aggressive',
    kind: 'info',
    priority: 'urgent',
    subject: `Pénalité contractuelle — ${p.contractName} (${p.missedDays} livraisons manquées)`,
    body:
      `Madame, Monsieur,\n\n` +
      `Conformément à la clause de service de notre contrat **${p.contractName}**, nous appliquons une pénalité de **${p.penalty.toLocaleString('fr-FR')} €** ` +
      `au titre des **${p.missedDays} livraisons non assurées** à ce jour (véhicule ou chauffeur indisponible).\n\n` +
      `Le montant est prélevé ce jour (J${day}). Sans amélioration rapide, nous reconsidérerons notre collaboration.${wom}\n\n` +
      `— Service achats, ${p.clientName}`,
    ttlDays: 30,
  };
}
